import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, setTokens } from '../../lib/api/client'
import type { Project, User, Workspace } from '../../lib/api/types'
import { navigate } from '../../app/App'

interface MeResponse {
  user: User
  workspaces: Workspace[]
}

export function ProjectList() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/auth/me') })
  const workspaceId = me.data?.workspaces[0]?.id

  const projects = useQuery({
    queryKey: ['projects', workspaceId],
    queryFn: () => api.get<Project[]>(`/projects?workspaceId=${workspaceId}`),
    enabled: Boolean(workspaceId),
  })

  const create = useMutation({
    mutationFn: (projectName: string) =>
      api.post<Project>('/projects', { workspaceId, name: projectName }),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setCreating(false)
      setName('')
      navigate(`/editor/${project.id}`)
    },
  })

  if (me.isLoading) return <FullPageMessage>Carregando…</FullPageMessage>
  if (me.isError) return <FullPageMessage>Não foi possível carregar sua conta.</FullPageMessage>

  const workspace = me.data?.workspaces[0]

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="border-b border-ink-750 bg-ink-850">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-3">
            <span className="text-lg font-bold tracking-tight">Easy Edit</span>
            {workspace && (
              <span className="text-xs text-ink-400">
                {workspace.name} · <span className="tnum">{workspace.aiCredits}</span> créditos de IA
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-400">{me.data?.user.name}</span>
            <button className="btn-ghost" onClick={() => setTokens(null)}>Sair</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        <div className="flex items-center justify-between gap-4 mb-5">
          <h1 className="text-xl font-bold tracking-tight">Projetos</h1>
          <button className="btn-primary" onClick={() => setCreating(true)}>Novo projeto</button>
        </div>

        {creating && (
          <form
            className="panel rounded-lg p-4 mb-5 flex gap-2 items-end"
            onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(name.trim()) }}
          >
            <label className="flex-1 flex flex-col gap-1.5">
              <span className="label">Nome do projeto</span>
              <input
                id="new-project-name"
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Podcast episódio 12"
                autoFocus
              />
            </label>
            <button type="submit" className="btn-primary py-2" disabled={create.isPending}>
              {create.isPending ? 'Criando…' : 'Criar'}
            </button>
            <button type="button" className="btn-ghost py-2" onClick={() => setCreating(false)}>
              Cancelar
            </button>
          </form>
        )}

        {projects.isLoading && <p className="text-sm text-ink-400">Carregando projetos…</p>}

        {projects.data?.length === 0 && !creating && (
          <div className="panel rounded-lg p-10 text-center">
            <p className="text-sm text-ink-300">Nenhum projeto ainda.</p>
            <p className="mt-1 text-xs text-ink-400">
              Crie um projeto, envie um vídeo e deixe a IA fazer o primeiro corte.
            </p>
          </div>
        )}

        <div className="grid gap-2">
          {projects.data?.map((project) => (
            <button
              key={project.id}
              onClick={() => navigate(`/editor/${project.id}`)}
              className="panel rounded-lg px-4 py-3 text-left hover:border-ink-600 transition-colors
                         flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{project.name}</div>
                <div className="mt-0.5 text-2xs text-ink-400 font-mono">
                  {project.width}×{project.height} · {Math.round(project.fpsNum / project.fpsDen)}fps
                  {project._count ? ` · ${project._count.exports} exportações` : ''}
                </div>
              </div>
              <StatusPill status={project.status} />
            </button>
          ))}
        </div>
      </main>
    </div>
  )
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  EM_EDICAO: { label: 'Em edição', className: 'bg-ink-750 text-ink-300' },
  AGUARDANDO_APROVACAO: { label: 'Aguardando aprovação', className: 'bg-accent-soft text-accent' },
  ALTERACOES_SOLICITADAS: { label: 'Alterações pedidas', className: 'bg-danger-soft text-danger' },
  APROVADO: { label: 'Aprovado', className: 'bg-teal-soft text-teal' },
  ARQUIVADO: { label: 'Arquivado', className: 'bg-ink-800 text-ink-400' },
}

function StatusPill({ status }: { status: string }) {
  const info = STATUS_LABELS[status] ?? STATUS_LABELS.EM_EDICAO!
  return (
    <span className={`shrink-0 rounded px-2 py-0.5 text-2xs font-medium ${info.className}`}>
      {info.label}
    </span>
  )
}

function FullPageMessage({ children }: { children: React.ReactNode }) {
  return <div className="h-full grid place-items-center text-sm text-ink-400">{children}</div>
}
